import { Link } from 'react-router-dom';
import { MapPin, Mail, Phone, Facebook, Instagram, Twitter, Heart, Waves } from 'lucide-react';

// Bomb Logo for Footer
const BombLogo = () => (
  <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none">
    <circle cx="12" cy="14" r="8" fill="white"/>
    <circle cx="9" cy="12" r="2" fill="currentColor" fillOpacity="0.2"/>
    <path d="M12 6 Q14 4, 17 3" stroke="white" strokeOpacity="0.7" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="18" cy="2" r="2" fill="#f97316"/>
    <circle cx="18" cy="2" r="3" fill="#fbbf24" fillOpacity="0.5"/>
  </svg>
);

// Inline bomb emoji SVG
const BombEmoji = ({ className = "w-7 h-7" }) => (
  <svg viewBox="0 0 36 36" className={className} fill="none" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
    <circle cx="18" cy="22" r="14" fill="#31373D"/>
    <ellipse cx="14" cy="18" rx="4" ry="5" fill="white" fillOpacity="0.2"/>
    <path d="M18 8 Q22 4, 28 2" stroke="#66757F" strokeWidth="3" strokeLinecap="round"/>
    <circle cx="30" cy="3" r="4" fill="#F4900C"/>
    <circle cx="30" cy="3" r="6" fill="#FFCC4D" fillOpacity="0.6"/>
  </svg>
);

export const Footer = () => {
  return (
    <footer className="bg-slate-900 relative overflow-hidden" data-testid="footer">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-500 via-cyan-500 to-teal-500" />
      <div className="absolute top-10 right-10 text-slate-800/30">
        <Waves className="w-40 h-40" />
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-6">
              <BombEmoji className="w-10 h-10" />
              <div>
                <span className="font-heading text-2xl font-bold text-white">Bombazoo</span>
                <p className="text-xs text-slate-500 tracking-wider">VIAJES INCREÍBLES</p>
              </div>
            </Link>
            <p className="text-slate-400 mb-6 max-w-md leading-relaxed">
              Tu plataforma de confianza para certificados de viaje a los destinos más populares. 
              Hacemos tus sueños de viaje realidad. 
              <span className="text-teal-400"> Viaja más, paga menos.</span>
            </p>
            <div className="flex items-center gap-3">
              <a href="#" className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center hover:bg-teal-600 transition-colors text-slate-400 hover:text-white">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center hover:bg-gradient-to-br hover:from-pink-500 hover:to-orange-500 transition-colors text-slate-400 hover:text-white">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center hover:bg-cyan-500 transition-colors text-slate-400 hover:text-white">
                <Twitter className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-6 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-teal-400" />
              Destinos Populares
            </h4>
            <ul className="space-y-3">
              {['Orlando', 'Miami', 'Cancún', 'Punta Cana', 'Las Vegas'].map(dest => (
                <li key={dest}>
                  <Link 
                    to={`/certificados?d=${encodeURIComponent(dest)}`} 
                    className="text-slate-400 hover:text-teal-400 transition-colors flex items-center gap-2"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-teal-500/50" />
                    {dest}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-6">Contacto</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href="https://wa.me/13057988806?text=%C2%A1Hola!%20Me%20interesa%20conocer%20m%C3%A1s%20sobre%20las%20ofertas%20de%20viaje." 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-slate-400 hover:text-green-400 transition-colors"
                >
                  <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-5 h-5 text-green-400 fill-current">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  +1 (305) 798-8806
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-slate-500 flex items-center gap-1">
            © {new Date().getFullYear()} Bombazoo. Hecho con 
            <Heart className="w-4 h-4 text-rose-500 fill-rose-500" /> 
            para viajeros
          </p>
          <div className="flex items-center gap-6 text-sm">
            <Link to="#" className="text-slate-500 hover:text-teal-400 transition-colors">Términos</Link>
            <Link to="#" className="text-slate-500 hover:text-teal-400 transition-colors">Privacidad</Link>
            <Link to="#" className="text-slate-500 hover:text-teal-400 transition-colors">Cookies</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
